/**
 * Hook for persisting Image Director conversations to database
 * Handles creating conversations, saving messages, and loading history
 */

import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  createConversation as dbCreateConversation,
  updateConversationTitle,
  endConversation as dbEndConversation,
  fetchMessages as dbFetchMessages,
  insertMessage as dbInsertMessage,
} from "@/services/db/conversations";

export interface PersistedMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  imageUrl?: string;
  createdAt: string;
}

export interface ConversationState {
  conversationId: string | null;
  messages: PersistedMessage[];
  loading: boolean;
}

export function useConversationPersistence() {
  const { user } = useAuth();
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Create a new conversation
  const createConversation = useCallback(async (title?: string): Promise<string | null> => {
    if (!user) return null;

    try {
      const id = await dbCreateConversation(user.id, title);
      setConversationId(id);
      return id;
    } catch (err) {
      console.error("Error creating conversation:", err);
      return null;
    }
  }, [user]);

  // Save a message to the current conversation
  const saveMessage = useCallback(async (
    role: "user" | "assistant",
    content: string,
    imageUrl?: string,
    overrideConversationId?: string
  ): Promise<boolean> => {
    const targetId = overrideConversationId || conversationId;
    if (!targetId || !user) return false;

    try {
      await dbInsertMessage({
        conversation_id: targetId,
        role,
        content,
        image_url: imageUrl || null,
      });
      return true;
    } catch (err) {
      console.error("Error saving message:", err);
      return false;
    }
  }, [conversationId, user]);

  // Update conversation title (after first user message)
  const updateTitle = useCallback(async (title: string): Promise<boolean> => {
    if (!conversationId || !user) return false;

    try {
      await updateConversationTitle(conversationId, title);
      return true;
    } catch (err) {
      console.error("Error updating conversation title:", err);
      return false;
    }
  }, [conversationId, user]);

  // Load messages for a specific conversation
  const loadConversation = useCallback(async (
    targetConversationId: string
  ): Promise<PersistedMessage[]> => {
    if (!user) return [];

    try {
      setLoading(true);
      const data = await dbFetchMessages(targetConversationId);

      setConversationId(targetConversationId);

      return data.map((msg) => ({
        id: msg.id,
        role: msg.role as "user" | "assistant",
        content: msg.content,
        imageUrl: msg.image_url || undefined,
        createdAt: msg.created_at,
      }));
    } catch (err) {
      console.error("Error loading conversation:", err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  // End current conversation (mark as ended)
  const endConversation = useCallback(async (): Promise<boolean> => {
    if (!conversationId || !user) return false;

    try {
      await dbEndConversation(conversationId);
      return true;
    } catch (err) {
      console.error("Error ending conversation:", err);
      return false;
    }
  }, [conversationId, user]);

  // Reset for new conversation
  const resetConversation = useCallback(() => {
    setConversationId(null);
  }, []);

  return {
    conversationId,
    loading,
    createConversation,
    saveMessage,
    updateTitle,
    loadConversation,
    endConversation,
    resetConversation,
  };
}
